import React, {useState, useEffect, useCallback} from 'react';
import {Form, Input, Button, Select, InputNumber, message, Upload, Tabs, Card, Row, Col, Divider, Image} from 'antd';
import {UploadOutlined} from '@ant-design/icons';
import {useNavigate, useParams} from 'react-router-dom';
import apiService from '../services/api';
import {testCurrentToken} from '../tokenTest';
import '../styles/AddFormula.css';
import ColorSelector from "./ColorSelector";

const {Option} = Select;

const hydrogenPeroxidePercent = [
    {name: 'hydrogenPeroxide3Percent', color: '双氧乳10VOL', position: 'A仓位'},
    {name: 'hydrogenPeroxide6Percent', color: '双氧乳20VOL2:1'},
    {name: 'hydrogenPeroxide9Percent', color: '双氧乳30VOL1:2'},
    {name: 'hydrogenPeroxide12Percent', color: '双氧乳40VOL', position: 'B仓位'}
];

const colorList = [
    {name: 'gray', color: '0/11灰色', english: 'Grey', top: 30},
    {name: 'green', color: '0/22绿色', english: 'Green'},
    {name: 'yellow', color: '0/33黄色', english: 'yellow'},
    {name: 'orange', color: '0/43橙色', english: 'Orange'},
    {name: 'red', color: '0/45红色', english: 'Red'},
    {name: 'purple', color: '0/66紫色', english: 'Purple'},
    {name: 'brown', color: '0/77棕色', english: 'Brown'},
    {name: 'blue', color: '0/88蓝色', english: 'Blue'},
    {name: 'black', color: '2/0黑色', english: 'Black'},
    {name: 'faded_bleached', color: '0/00淡化剂', english: 'Faded'},
    {name: 'bleached', color: '12/11极浅灰金色', english: 'Bleached', top: 30},
    {name: 'silver', color: '12/16极浅紫金', english: 'Silver'},
    {name: 'gold', color: '浅棕色', english: 'Gold'},
    {name: 'linen', color: '深灰亚麻色', english: 'Linen'},
    {name: 'custom1', color: '自定义仓位1', english: 'Custom1'},
    {name: 'custom2', color: '自定义仓位2', english: 'Custom2'},
    {name: 'custom3', color: '自定义仓位3', english: 'Custom3'},
    {name: 'custom4', color: '自定义仓位4', english: 'Custom4'}
];

const EditFormula = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [imageLoading, setImageLoading] = useState(false);
    const [imageBase64, setImageBase64] = useState([null, null, null]);
    const [colorOptions, setColorOptions] = useState([]); // State is now self-contained
    const [colorTypes, setColorTypes] = useState([]);
    const navigate = useNavigate();
    const {id} = useParams();

    useEffect(() => {
        const fetchColorType = async () => {
            const response = await apiService.formula.getColorType();
            setColorTypes(response.data);
        };
        fetchColorType();
    }, []);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await apiService.formula.getCategory();
                if (response.code === 200 && response.data) {
                    const newOptions = response.data.map(category => ({
                        key: category,
                        label: category
                    }));
                    setColorOptions(newOptions); // Set state directly
                }
            } catch (error) {
                console.error("获取分类失败:", error);
            }
        };
        fetchCategories();
    }, []);

    // Load formula data
    useEffect(() => {
        const fetchFormula = async () => {
            try {
                const response = await apiService.formula.getDetail(id);
                if (response.code === 200) {
                    const data = response.data;
                    const initialValues = {};

                    if (data.categoryName) {
                        setColorOptions(previous => previous.some(option => option.label === data.categoryName)
                            ? previous
                            : [...previous, { key: data.categoryName, label: data.categoryName }]);
                    }

                    // Ensure all components have a default value of 0
                    colorList.forEach(c => initialValues[c.name] = 0);
                    hydrogenPeroxidePercent.forEach(d => initialValues[d.name] = 0);

                    // Set form initial values by merging defaults with fetched data
                    form.setFieldsValue({
                        ...initialValues,
                        name: data.name,
                        description: data.description,
                        colorType: data.colorType,
                        categoryName: data.categoryName,
                        totalWeight: data.gramWeight,
                        ...data.colorComponents,
                        ...data.developerComponents,
                    });

                    // Handle images
                    const newImages = [null, null, null];
                    if (data.iconPaths && data.iconPaths.length > 0) {
                        data.iconPaths.forEach((url, index) => {
                            if (index < 3) {
                                newImages[index] = { uid: `existing-${index}`, url, status: 'done', name: `图片${index + 1}` };
                            }
                        });
                    }
                    setImageBase64(newImages);
                    
                    // Trigger calculation after setting values
                    calculateTotalWeight();
                }
            } catch (error) {
                console.error("获取配方失败:", error);
                message.error("获取配方信息失败");
            }
        };
        fetchFormula();
    }, [id, form]);

    const getBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    };

    const handleImageUpload = async (info, position) => {
        if (imageLoading) return;
        setImageLoading(true);

        try {
            if (info.file.originFileObj) {
                const base64 = await getBase64(info.file.originFileObj);
                const newImage = {
                    uid: info.file.uid,
                    url: base64,
                    status: 'done',
                    name: info.file.name
                };

                setImageBase64(prev => {
                    const newImages = [...prev];
                    newImages[position] = newImage;
                    return newImages;
                });
            }
        } catch (error) {
            message.error('图片处理失败');
        } finally {
            setImageLoading(false);
        }
    };

    const handleImageRemove = (position) => {
        setImageBase64(prev => {
            const newImages = [...prev];
            newImages[position] = null;
            return newImages;
        });
    };

    const calculateTotalWeight = useCallback(() => {
        const values = form.getFieldsValue();
        let total = 0;

        colorList.forEach((color) => {
            total += Number(values[color.name]) || 0;
        });

        hydrogenPeroxidePercent.forEach((developer) => {
            total += Number(values[developer.name]) || 0;
        });
        
        const finalTotal = parseFloat(total.toFixed(2));
        form.setFieldsValue({ totalWeight: finalTotal });
    }, [form]);

    const customRequest = ({onSuccess}) => onSuccess("ok");

    const onFinish = async (values) => {
        setLoading(true);

        const formulaData = {
            id,
            name: values.name,
            description: values.description || '',
            colorType: values.colorType,
            categoryName: values.categoryName,
            iconPath: imageBase64.map(img => img ? img.url : null).filter(url => url !== null),
            gramWeight: Number(values.totalWeight) || 0.00,
            colorComponents: {
                gray: Number(values.gray) || 0.00,
                green: Number(values.green) || 0.00,
                yellow: Number(values.yellow) || 0.00,
                orange: Number(values.orange) || 0.00,
                red: Number(values.red) || 0.00,
                purple: Number(values.purple) || 0.00,
                brown: Number(values.brown) || 0.00,
                blue: Number(values.blue) || 0.00,
                black: Number(values.black) || 0.00,
                faded_bleached: Number(values.faded_bleached) || 0.00,
                bleached: Number(values.bleached) || 0.00,
                silver: Number(values.silver) || 0.00,
                gold: Number(values.gold) || 0.00,
                linen: Number(values.linen) || 0.00,
                custom1: Number(values.custom1) || 0.00,
                custom2: Number(values.custom2) || 0.00,
                custom3: Number(values.custom3) || 0.00,
                custom4: Number(values.custom4) || 0.00
            },
            developerComponents: {
                hydrogenPeroxide3Percent: Number(values.hydrogenPeroxide3Percent) || 0.00,
                hydrogenPeroxide6Percent: Number(values.hydrogenPeroxide6Percent) || 0.00,
                hydrogenPeroxide9Percent: Number(values.hydrogenPeroxide9Percent) || 0.00,
                hydrogenPeroxide12Percent: Number(values.hydrogenPeroxide12Percent) || 0.00,
            }
        };

        try {
            const response = await apiService.formula.update(formulaData.id, formulaData);
            if (response.code === 200) {
                message.success('配方更新成功！');
                navigate('/formulas');
            } else {
                message.error(response.message);
            }
        } catch (error) {
            message.error(error?.message || '更新失败');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="add-formula-container">
            <Card title="修改配方" className="formula-card">
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    onValuesChange={calculateTotalWeight}
                >
                    <Col span={24}>
                        <Form.Item name="name" label="配方名称" rules={[{ required: true, message: '配方名不能为空' }]}>
                            <Input placeholder="请输入配方名称"/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item name="colorType" label="色板类型" rules={[{ required: true, message: '色板类型不能为空' }]}>
                            <Select>
                                {colorTypes.map(type => (
                                    <Option key={type} value={type}>{type}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col span={24}>
                        <Form.Item label="总克重" name="totalWeight" rules={[{ required: true, message: '总克重不能为空' }]}>
                            <InputNumber style={{width: '100%'}} precision={2} addonAfter="g" disabled />
                        </Form.Item>
                    </Col>

                    <Row gutter={24}>
                        <Col span={24}>
                            <Form.Item name="categoryName" label="分类名称" rules={[{required: true}]}>
                                <ColorSelector colors={colorOptions.filter(c => c && c.label)}/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="description" label="配方说明" rules={[{required: true, message: '描述配方不能为空'}]}>
                        <Input.TextArea rows={3}/>
                    </Form.Item>

                    <Form.Item
                        name="upload"
                        label="上传图片（最多3张）"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => e?.fileList || []}
                    >
                        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'left'}}>
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '16px',
                                marginBottom: '16px',
                                minHeight: '120px',
                                padding: '16px',
                                border: '1px dashed #d9d9d9',
                                borderRadius: '6px',
                                backgroundColor: '#fafafa'
                            }}>
                                {[0, 1, 2].map((position) => (
                                    <div key={position} style={{position: 'relative'}}>
                                        {imageBase64[position] ? (
                                            <>
                                                <Image
                                                    height={100}
                                                    width={100}
                                                    src={imageBase64[position].url}
                                                    alt={`配方预览${position + 1}`}
                                                    style={{
                                                        objectFit: 'cover',
                                                        border: '1px solid #d9d9d9',
                                                        borderRadius: '4px'
                                                    }}
                                                    onError={(e) => console.error('图片加载失败', e)}
                                                />
                                                <Button
                                                    type="text"
                                                    danger
                                                    size="small"
                                                    style={{
                                                        position: 'absolute',
                                                        top: '-8px',
                                                        right: '-8px',
                                                        background: '#ff4d4f',
                                                        color: 'white',
                                                        borderRadius: '50%',
                                                        width: '20px',
                                                        height: '20px',
                                                        padding: 0,
                                                        fontSize: '12px'
                                                    }}
                                                    onClick={() => handleImageRemove(position)}
                                                >
                                                    ×
                                                </Button>
                                            </>
                                        ) : (
                                            <div style={{
                                                width: '100px',
                                                height: '100px',
                                                border: '2px dashed #d9d9d9',
                                                borderRadius: '4px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: '#fafafa',
                                                cursor: 'pointer'
                                            }}>
                                                <Upload
                                                    listType="picture"
                                                    onChange={(info) => handleImageUpload(info, position)}
                                                    customRequest={customRequest}
                                                    beforeUpload={(file) => {
                                                        const isValid = file.type.startsWith('image/') && file.size / 1024 / 1024 < 2;
                                                        if (!isValid) message.error('请上传小于2MB的图片');
                                                        return isValid;
                                                    }}
                                                    showUploadList={false}
                                                    accept="image/*"
                                                >
                                                    <div style={{
                                                        textAlign: 'center',
                                                        color: '#999',
                                                        fontSize: '12px'
                                                    }}>
                                                        <UploadOutlined
                                                            style={{fontSize: '20px', marginBottom: '4px'}}/>
                                                        <div>点击上传</div>
                                                    </div>
                                                </Upload>
                                            </div>
                                        )}
                                        <div style={{
                                            textAlign: 'center',
                                            fontSize: '12px',
                                            color: '#666',
                                            marginTop: '4px'
                                        }}>
                                            图片{position + 1}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div style={{
                                fontSize: '12px',
                                color: '#666',
                                textAlign: 'center',
                                marginTop: '4px'
                            }}>
                                💡 提示：在文件选择对话框中，双击图片文件即可快速选择。点击图片右上角的 × 可删除图片
                            </div>
                        </div>
                    </Form.Item>

                    <Divider orientation="left">配方成分</Divider>

                    <Row gutter={24}>
                        {hydrogenPeroxidePercent.map(developer => (
                            <Col xs={24} sm={24} key={developer.name}>
                                <Form.Item
                                    name={developer.name}
                                    label={developer.color}
                                >
                                    <InputNumber
                                        style={{width: '100%'}}
                                        placeholder={`${developer.position ? developer.position : '计算仓位'}`}
                                        min={0}
                                        precision={2}
                                        addonAfter="g"
                                    />
                                </Form.Item>
                            </Col>
                        ))}
                    </Row>

                    <Row gutter={24}>
                        {colorList.map((color, index) => (
                            <Col xs={24} sm={24} key={color.name}>
                                <Form.Item
                                    name={color.name}
                                    label={color.english ? `${color.color}/${color.english}` : color.color}
                                    style={{marginTop: color.top ? color.top : 0}}
                                >
                                    <InputNumber
                                        style={{width: '100%'}}
                                        placeholder={`${index + 1}号仓位`}
                                        min={0}
                                        precision={2}
                                        addonAfter="g"
                                    />
                                </Form.Item>
                            </Col>
                        ))}
                    </Row>

                    <Form.Item style={{marginTop: 24}}>
                        <Button onClick={() => navigate(-1)} style={{marginRight: 16}}>
                            取消
                        </Button>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            修改配方
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default EditFormula;
